import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { FIRM_TYPES, FIRM_STATUSES } from '@shared/types';
import { api, ApiError, qk } from '@client/lib/api';
import { useAdminFirms, type AdminFirm } from '@client/lib/admin-api';
import { PageHeader } from '@client/components/admin/shared';
import { Button } from '@client/components/ui/button';
import { Dialog } from '@client/components/ui/overlay';
import { Field, Input, Select, Textarea } from '@client/components/ui/input';
import { Spinner } from '@client/components/ui/feedback';
import { Badge } from '@client/components/ui/badge';
import { Table, Td, Th, Tr } from '@client/components/ui/table';
import { useToast } from '@client/components/ui/toast';
import { fmtRelative } from '@client/lib/format';
import { AddInvestorDialog } from './investors.index';

export const Route = createFileRoute('/admin/firms')({ component: FirmsPage });

function FirmsPage() {
  const firms = useAdminFirms();
  const qc = useQueryClient();
  const toast = useToast();
  const [edit, setEdit] = React.useState<Partial<AdminFirm> | null>(null);
  const [addPerson, setAddPerson] = React.useState<string | null>(null);
  const save = useMutation({
    mutationFn: (f: Partial<AdminFirm>) => (f.id ? api(`/api/admin/firms/${f.id}`, { method: 'PATCH', body: f }) : api('/api/admin/firms', { body: f })),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.admin('firms') }); setEdit(null); toast.push({ tone: 'good', title: 'Saved' }); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not save', body: e instanceof ApiError ? e.code : String(e) }),
  });
  return (
    <>
      <PageHeader title="Firms" eyebrow="VCs, angel groups, family offices, corporates" actions={<Button onClick={() => setEdit({ name: '', type: 'vc', status: 'prospect' })}><Plus />New firm</Button>} />
      <div className="px-6 py-6 md:px-8">
        {firms.isPending ? <Spinner /> : (
          <div className="rounded-card border border-border bg-card">
            <Table>
              <thead><tr><Th>Firm</Th><Th>Type</Th><Th>Status</Th><Th align="right">People</Th><Th align="right">Active grants</Th><Th align="right">Engagement 30d</Th><Th>Last seen</Th><Th /></tr></thead>
              <tbody>
                {firms.data?.firms.map((f) => (
                  <Tr key={f.id}>
                    <Td><button className="font-[520] text-brand" onClick={() => setEdit(f)}>{f.name}</button>{f.country ? <span className="ml-2 text-muted">{f.country}</span> : null}</Td>
                    <Td>{f.type}</Td>
                    <Td><Badge tone={f.status === 'active' ? 'good' : f.status === 'passed' ? 'neutral' : 'info'}>{f.status}</Badge></Td>
                    <Td align="right">{f.people}{f.active_people ? <span className="ml-1 text-muted">· {f.active_people} active</span> : null}</Td>
                    <Td align="right">{f.active_grants}</Td>
                    <Td align="right"><span className="inline-block min-w-8 rounded-full bg-soft px-2 text-[12px] font-[570] text-brand">{f.engagement ?? 0}</span>{f.top_person ? <span className="ml-2 text-muted" title={`Most engaged: ${f.top_person.name} (${f.top_person.score})`}>{f.top_person.name.split(' ')[0]}</span> : null}</Td>
                    <Td className="text-muted">{f.last_seen_at ? fmtRelative(f.last_seen_at, 'en') : 'never'}</Td>
                    <Td className="text-right"><Button size="sm" variant="ghost" onClick={() => setAddPerson(f.id)}>Add person</Button></Td>
                  </Tr>
                ))}
                {firms.data?.firms.length === 0 ? <tr><Td colSpan={8} className="py-8 text-center text-muted">No firms yet.</Td></tr> : null}
              </tbody>
            </Table>
          </div>
        )}
      </div>
      {edit ? (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)} title={edit.id ? 'Edit firm' : 'New firm'} footer={<><Button variant="secondary" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={() => save.mutate(edit)} disabled={!edit.name?.trim()} loading={save.isPending}>Save</Button></>}>
          <Field label="Name" htmlFor="f-name"><Input id="f-name" autoFocus value={edit.name ?? ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Type" htmlFor="f-type"><Select id="f-type" value={edit.type ?? 'vc'} onChange={(e) => setEdit({ ...edit, type: e.target.value })}>{FIRM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
            <Field label="Status" htmlFor="f-status"><Select id="f-status" value={edit.status ?? 'prospect'} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>{FIRM_STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
            <Field label="Country (ISO-2)" htmlFor="f-country"><Input id="f-country" maxLength={2} value={edit.country ?? ''} onChange={(e) => setEdit({ ...edit, country: e.target.value.toUpperCase() })} /></Field>
          </div>
          <Field label="Website" htmlFor="f-web"><Input id="f-web" value={edit.website ?? ''} onChange={(e) => setEdit({ ...edit, website: e.target.value })} placeholder="https://" /></Field>
          <Field label="Notes" htmlFor="f-notes"><Textarea id="f-notes" value={edit.notes ?? ''} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></Field>
        </Dialog>
      ) : null}
      {addPerson ? <AddInvestorDialog firmId={addPerson} onClose={() => setAddPerson(null)} /> : null}
    </>
  );
}
